import React from 'react';
import moment from 'moment';
import Select from 'react-select';
import { Controller, useForm } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import DatePicker from '@amir04lm26/react-modern-calendar-date-picker';

import { list as listUsers } from '../../apis/users';
import { capitalize } from '../../helpers/formatters';
import { OrderFormCart } from './OrderFormCart';
import { mixClasses } from '../../helpers/utilities';

const statusOptions = ['Open', 'Paid', 'Closed', 'Cancelled'].map((status) => ({
    label: status,
    value: status
}));

export const OrderForm = ({ model: order = null, mode = 'show', className, onSubmit, onReset }) => {
    const defaultValues = {
        date: null,
        user: null,
        status: null,
        items: []
    };

    if (order) {
        const dateMoment = moment(order.date);
        defaultValues.date = {
            year: dateMoment.year(),
            month: dateMoment.month() + 1,
            day: dateMoment.date()
        };
        defaultValues.user = {
            value: order.user.id,
            label: order.user.name
        };
        defaultValues.status = {
            value: order.status,
            label: order.status
        };
        defaultValues.items = order.items;
    }

    const {
        formState: { errors },
        handleSubmit,
        control,
        watch
    } = useForm({
        defaultValues
    });

    const validations = {
        date: {
            required: 'Date is required.'
        },
        user: {
            required: 'User is required.'
        },
        status: {
            required: 'Status is required.'
        },
        items: {
            required: {
                value: (items) => items.length,
                message: 'Please add at least an item.'
            }
        }
    };

    const loadUserOptions = (inputValue, callback) => {
        listUsers({ filter: inputValue })
            .then((resp) => {
                let users = [];
                if (resp.success) {
                    users = resp.data.records.map((obj) => ({
                        value: obj.id,
                        label: obj.name
                    }));
                }
                callback(users);
            })
            .catch(console.log);
    };

    const [date] = watch(['date']);
    const renderCalendarInput = ({ ref }) => {
        let value = '';
        if (date) {
            value = moment(new Date(date.year, date.month - 1, date.day)).format('YYYY/MM/DD');
        }
        return (
            <input
                ref={ref}
                type="text"
                readOnly={true}
                name="calendar-input"
                placeholder="Select..."
                className="form-control w-100"
                value={value}
            />
        );
    };

    const handleBeforeSubmit = ({ date, user, status, items }) => {
        onSubmit({
            date: new Date(date.year, date.month - 1, date.day),
            status: status.value,
            user: user.value,
            items
        });
    };

    return (
        <form
            className={mixClasses(['order-form', className])}
            onSubmit={handleSubmit(handleBeforeSubmit)}
            onReset={onReset}>
            <div className="row">
                <div className="col-12 col-xl-4 mb-3">
                    <label htmlFor="orderDate" className="form-label">
                        Date
                    </label>
                    <Controller
                        control={control}
                        rules={validations.date}
                        name="date"
                        render={({
                            field: { onChange, onBlur, value, name, ref },
                            fieldState: { invalid, isTouched, isDirty, error },
                            formState
                        }) => (
                            <DatePicker
                                value={value}
                                onChange={onChange}
                                renderInput={renderCalendarInput}
                                className="d-flex w-100"
                                calendarClassName="responsive-calendar"
                            />
                        )}
                    />
                    {errors.date && (
                        <div className="invalid-feedback d-block">{errors.date.message}</div>
                    )}
                </div>
                <div className="col-12 col-xl-4 mb-3">
                    <label htmlFor="orderUser" className="form-label">
                        User
                    </label>
                    <Controller
                        control={control}
                        rules={validations.user}
                        name="user"
                        render={({
                            field: { onChange, onBlur, value, name, ref },
                            fieldState: { invalid, isTouched, isDirty, error },
                            formState
                        }) => (
                            <AsyncSelect
                                cacheOptions
                                defaultOptions
                                isDisabled={mode === 'show'}
                                loadOptions={loadUserOptions}
                                classNamePrefix="react-select"
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                name={name}
                                ref={ref}
                            />
                        )}
                    />
                    {errors.user && (
                        <div className="invalid-feedback d-block">{errors.user.message}</div>
                    )}
                </div>
                <div className="col-12 col-xl-4 mb-3">
                    <label htmlFor="orderStatus" className="form-label">
                        Status
                    </label>
                    <Controller
                        control={control}
                        rules={validations.status}
                        name="status"
                        render={({
                            field: { onChange, onBlur, value, name, ref },
                            fieldState: { invalid, isTouched, isDirty, error },
                            formState
                        }) => (
                            <Select
                                isMulti={false}
                                isLoading={false}
                                isDisabled={mode === 'show'}
                                classNamePrefix="react-select"
                                options={statusOptions}
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                name={name}
                                ref={ref}
                            />
                        )}
                    />
                    {errors.status && (
                        <div className="invalid-feedback d-block">{errors.status.message}</div>
                    )}
                </div>
                <div className="col-12 mb-3">
                    <Controller
                        control={control}
                        rules={validations.items}
                        name="items"
                        render={({
                            field: { onChange, onBlur, value, name, ref },
                            fieldState: { invalid, isTouched, isDirty, error },
                            formState
                        }) => (
                            <OrderFormCart
                                isDisabled={mode === 'show'}
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                name={name}
                            />
                        )}
                    />
                    {errors.items && (
                        <div className="invalid-feedback d-block">{errors.items.message}</div>
                    )}
                </div>
            </div>
            {mode === 'show' && (
                <div className="d-grid col-sm-6 col-md-4 col-lg-3">
                    <button onClick={onReset} type="button" className="btn btn-outline-secondary">
                        Close
                    </button>
                </div>
            )}
            {mode !== 'show' && (
                <div className="row mb-3">
                    <div className="d-grid order-1 order-sm-0 col-sm-6 col-md-4 col-lg-3 mb-3 mb-sm-0">
                        <button className="btn btn-outline-secondary" type="reset">
                            Cancel
                        </button>
                    </div>
                    <div className="d-grid order-0 order-sm-1 col-sm-6 col-md-4 col-lg-3 offset-md-4 offset-lg-6 mb-3 mb-sm-0">
                        <button className="btn btn-primary" type="submit">
                            {capitalize(mode)}
                        </button>
                    </div>
                </div>
            )}
        </form>
    );
};
